'use client';
import { AxiosError } from 'axios';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { FaArrowLeftLong } from 'react-icons/fa6';

import { getCookie } from '@/lib/cookies';
import { APIErrorResponseGenerator } from '@/lib/helper';
import handleGA4Event from '@/lib/service-workers';

import Button from '@/components/@base/button';
import Container from '@/components/@base/container';
import Group from '@/components/@base/input/@shared/group';
import Label from '@/components/@base/input/@shared/label';
import CheckBox from '@/components/@base/input/check-box';
import { showToast } from '@/components/@base/toast/show-toast';
import { Text } from '@/components/@base/typography';
import UseUserAuth from '@/components/app/cart/services/helper';
import { useGetCart, usePostCheckout } from '@/components/app/cart/services/hooks';
import { useGetProfile } from '@/components/layout/services/hooks';

import { useCheckoutStore } from '@/store/checkout.store';

import ICON_SIZE from '@/constant/icon-size-color';
import { pageLevelLocalization } from '@/constant/localization';

const { cart: cartLocalization } = pageLevelLocalization;

export default function Actions(props) {
  const [rulesAccepted, setRulesAccepted] = useState(false);
  const authModal = UseUserAuth();
  const { mutate, data: checkoutInfo, isPending } = usePostCheckout();
  const { isAuthenticated } = useGetProfile();
  const { gateway, useWallet } = useCheckoutStore();
  const { data, isLoading } = useGetCart();

  useEffect(() => {
    if (checkoutInfo && !checkoutInfo.redirect_uri) {
      const submitForm = () => {
        const form = document.getElementById('hiddenForm') as HTMLFormElement;
        form?.submit();
      };

      const timeoutId = setTimeout(() => {
        submitForm();
      }, 1000);

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [checkoutInfo]);

  const handleCheckout = () => {
    if (!isAuthenticated) {
      authModal();
    } else if (!rulesAccepted) {
      showToast({
        message: cartLocalization.toContinuePurchaseYouHaveToFirstlyReadAndAcceptRules,
      });
    } else {
      const via = getCookie('via');
      handleGA4Event(
        'begin_checkout',
        data?.products?.map((item) => ({
          item_id: item?.id,
          item_name: item?.title,
          price: item?.price,
          quantity: item?.quantity,
          affiliation: item?.store?.slug,
          discount: item?.discount,
        })),
        {
          currency: 'USD',
          value: data?.total,
        },
      );
      let body = { gateway };
      if (useWallet) {
        body['use_wallet'] = true;
      }
      if (via) {
        body['via'] = via?.affId;
        body['referrer'] = via.referrer;
      }
      mutate(
        body,

        {
          onSuccess: (res) => {
            if (res.redirect_uri) {
              window.location.href = res.redirect_uri;
            }
          },
          onError: (e) => {
            if (e instanceof AxiosError) {
              showToast(APIErrorResponseGenerator(e));
            }
          },
        },
      );
    }
  };

  if (data?.products?.length === 0 || isLoading) {
    return null;
  }

  return (
    <Container className='w-full px-[10px] pt-[19px] md:px-0 *:w-full' center>
      <Container center className='max-w-[350px] flex-col items-start'>
        <Group className='flex items-center'>
          <CheckBox
            onChange={() => setRulesAccepted(!rulesAccepted)}
            checked={rulesAccepted}
            className='h-4 w-4'
          />
          <Label className='pr-[10px]'>
            <Text className='text-[#5B5C60]'>
              قوانین و{' '}
              <Link
                target='_blank'
                prefetch={false}
                href='/content/terms'
                rel='noopener noreferrer'
                onClick={(e) => e.stopPropagation()}
                className='text-[#FF9606]'
              >
                شرایط ژاکت
              </Link>{' '}
              را مطالعه کردم
            </Text>
          </Label>
        </Group>
        <Button
          onClick={handleCheckout}
          isLoading={isPending}
          className='mt-[29px] h-[59px] w-full bg-[#81CF33] text-base text-[#F9FAFC] shadow-[0px_5px_25px_0px_rgba(52,168,85,0.17)] hover:bg-[#F5FBED] hover:text-[#81CF33]'
        >
          {cartLocalization.submitAndPay}
          <FaArrowLeftLong size={ICON_SIZE.lg} className='pr-2' />
        </Button>
      </Container>
      {checkoutInfo && (
        <form
          id='hiddenForm'
          action={checkoutInfo.url}
          method={checkoutInfo.type}
          style={{ display: 'none' }}
        >
          {checkoutInfo?.params &&
            Object.keys(checkoutInfo?.params).map((key, index) => (
              <input key={index} type='hidden' name={key} value={checkoutInfo.params[key]} />
            ))}
        </form>
      )}
    </Container>
  );
}
